import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import ButtonBase from '@mui/material/ButtonBase';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import CheckCircleOutlineRounded from '@mui/icons-material/CheckCircleOutlineRounded';
import AutoStoriesOutlined from '@mui/icons-material/AutoStoriesOutlined';
import { YomiriCover } from './YomiriCover.tsx';
import { yomiriDatabase } from '../../storage/YomiriDatabase.ts';
import { sourceProvider } from '../../core/SourceProvider.ts';
import type { YomiriManga, YomiriChapter } from '../../types/extension.ts';

export interface RecentUpdateItem {
  manga: YomiriManga;
  chapter: YomiriChapter;
}

interface Props {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onNavigate: (view: string) => void;
  onOpenManga?: (id: string, chapterId?: string, manga?: YomiriManga) => void;
  items: RecentUpdateItem[];
  loading: boolean;
}

export function YomiriNotificationsPopover({
  anchorEl,
  open,
  onClose,
  onNavigate,
  onOpenManga,
  items,
  loading,
}: Props) {
  const displayItems = items.slice(0, 5);

  const handleItemClick = (item: RecentUpdateItem) => {
    onClose();
    if (onOpenManga) {
      onOpenManga(item.manga.id, item.chapter.id, item.manga);
    } else {
      onNavigate('updates');
    }
  };

  const handleSeeAll = () => {
    onClose();
    onNavigate('updates');
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      slotProps={{
        paper: {
          sx: {
            width: 360,
            maxWidth: '92vw',
            mt: 1.2,
            bgcolor: 'var(--y-containerLow, #16151f)',
            color: 'var(--y-onSurface, #ffffff)',
            borderRadius: '10px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 16px 40px rgba(0, 0, 0, 0.6)',
            overflow: 'hidden',
          },
        },
      }}
    >
      {/* Popover Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          bgcolor: 'rgba(255, 255, 255, 0.02)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoStoriesOutlined sx={{ fontSize: 18, color: 'var(--y-primary, #ff375f)' }} />
          <Typography sx={{ fontSize: 14, fontWeight: 700, letterSpacing: '-0.01em' }}>
            Novedades de la biblioteca
          </Typography>
        </Box>
        {items.length > 0 && (
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              px: 1,
              py: 0.2,
              borderRadius: '10px',
              bgcolor: 'rgba(255, 55, 95, 0.15)',
              color: 'var(--y-primary, #ff375f)',
            }}
          >
            {items.length} {items.length === 1 ? 'nuevo' : 'nuevos'}
          </Typography>
        )}
      </Box>

      {/* Popover Content */}
      <Box sx={{ maxHeight: 380, overflowY: 'auto', p: 1 }}>
        {loading && items.length === 0 ? (
          <Box sx={{ py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
            <CircularProgress size={24} sx={{ color: 'var(--y-primary, #ff375f)' }} />
            <Typography sx={{ fontSize: 12, color: 'var(--y-onSurfaceVariant, #a0a0ab)' }}>
              Comprobando capítulos nuevos…
            </Typography>
          </Box>
        ) : displayItems.length === 0 ? (
          <Box sx={{ py: 4, px: 2, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <CheckCircleOutlineRounded sx={{ fontSize: 32, color: 'rgba(52, 211, 153, 0.8)' }} />
            <Typography sx={{ fontSize: 13, fontWeight: 650, color: 'var(--y-onSurface)' }}>
              Estás al día
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'var(--y-onSurfaceVariant, #a0a0ab)', maxWidth: 240, lineHeight: 1.4 }}>
              No hay capítulos pendientes por leer en tu biblioteca.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {displayItems.map((item) => {
              const key = `${item.manga.sourceId}:${item.chapter.id}`;
              const date = item.chapter.uploadDate ? new Date(item.chapter.uploadDate) : null;
              const formattedDate = date && !Number.isNaN(date.getTime())
                ? date.toLocaleDateString('es-AR', { day: 'numeric', month: 'short' })
                : '';

              return (
                <ButtonBase
                  key={key}
                  onClick={() => handleItemClick(item)}
                  sx={{
                    width: '100%',
                    p: 1.2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.4,
                    textAlign: 'left',
                    borderRadius: '6px',
                    bgcolor: 'transparent',
                    transition: 'background-color 0.15s ease',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.05)',
                    },
                  }}
                >
                  <YomiriCover src={item.manga.coverUrl} width={42} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      noWrap
                      sx={{
                        fontSize: 13.5,
                        fontWeight: 650,
                        color: 'var(--y-onSurface)',
                        lineHeight: 1.3,
                      }}
                    >
                      {item.manga.title}
                    </Typography>
                    <Typography
                      noWrap
                      sx={{
                        fontSize: 12,
                        color: 'var(--y-primary, #ff375f)',
                        fontWeight: 600,
                        mt: 0.3,
                      }}
                    >
                      {item.chapter.name}
                    </Typography>
                    {formattedDate && (
                      <Typography sx={{ fontSize: 10.5, color: 'var(--y-onSurfaceVariant, #a0a0ab)', mt: 0.2 }}>
                        {formattedDate}
                      </Typography>
                    )}
                  </Box>
                </ButtonBase>
              );
            })}
          </Box>
        )}
      </Box>

      {/* Popover Footer */}
      <Box
        sx={{
          p: 1.2,
          borderTop: '1px solid rgba(255, 255, 255, 0.08)',
          bgcolor: 'rgba(255, 255, 255, 0.02)',
        }}
      >
        <Button
          fullWidth
          size="small"
          onClick={handleSeeAll}
          endIcon={<ArrowForwardRounded sx={{ fontSize: 16 }} />}
          sx={{
            py: 0.8,
            fontSize: 12.5,
            fontWeight: 700,
            color: 'var(--y-onSurface)',
            bgcolor: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: '6px',
            textTransform: 'none',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          Ver todas las novedades
        </Button>
      </Box>
    </Popover>
  );
}
